'use client';

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createEventBus, getFirebaseAuth } from '@/shared/utils';
import { BackgroundWavesScreen } from '@/shared/ui';
import { Onboarding } from '@/shared/i18n';
import { OnboardingEventKeys, OnboardingEvents, OnboardingScreen } from '@/profile';
import { useRouter } from 'next/navigation';
import { AuthContext } from '@/auth';

interface OnboardingContainerProps {
  dict: Onboarding;
  stepMode?: boolean;
  step?: number;
  userId?: number;
}

function OnboardingContainer({ dict, stepMode = false, step }: OnboardingContainerProps) {
  const onboardingEventBus = createEventBus<OnboardingEvents>('onboarding-scope');
  const [viewReady, setViewReady] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const router = useRouter();
  const { user } = useContext(AuthContext);

  const handleOnboardingView = useCallback(() => {
    setViewReady(true);
  }, []);

  // handleStepNavigation esiste già
  const handleStepNavigation = useCallback(() => {
    router.push('/profilo/onboarding/step/1');
  }, [router]);

  // Creiamo handleStep2
  const handleStep2Navigation = useCallback(() => {
    router.push('/profilo/onboarding/step/2');
  }, [router]);

  // Creiamo handleStep2
  const handleStep3Navigation = useCallback(() => {
    router.push('/profilo/onboarding/step/3');
  }, [router]);

  const handleInstrumentChildDone = useCallback(() => {
    router.push('/profilo/onboarding/step/2');
  }, [router]);

  const handleCatalogRedirect = useCallback(() => {
    router.push('/catalogo');
  }, [router]);

  useEffect(() => {
    async function fetchAuthToken() {
      try {
        const auth = await getFirebaseAuth();
        const token = await auth.currentUser?.getIdToken(true);
        setAccessToken(token || null);
      } catch (error) {
        console.error('Errore nel recupero del token:', error);
      }
    }
    fetchAuthToken();
  }, []);

  useEffect(() => {
    onboardingEventBus.on(OnboardingEventKeys.start.view, handleOnboardingView);
    onboardingEventBus.on(OnboardingEventKeys.start.do, handleStepNavigation);

    // Ascoltiamo l'evento per step 2 (StudySession)
    onboardingEventBus.on(OnboardingEventKeys.studySessions.view, handleStep2Navigation);
    onboardingEventBus.on(OnboardingEventKeys.instrument.child.done, handleInstrumentChildDone);
    onboardingEventBus.on(OnboardingEventKeys.levelSelection.view, handleStep3Navigation);
    onboardingEventBus.on(OnboardingEventKeys.end.redirect, handleCatalogRedirect);

    return () => {
      onboardingEventBus.off(OnboardingEventKeys.start.view, handleOnboardingView);
      onboardingEventBus.off(OnboardingEventKeys.start.do, handleStepNavigation);
      onboardingEventBus.off(OnboardingEventKeys.studySessions.view, handleStep2Navigation);
      onboardingEventBus.off(OnboardingEventKeys.instrument.child.done, handleInstrumentChildDone);
      onboardingEventBus.off(OnboardingEventKeys.levelSelection.view, handleStep3Navigation);
      onboardingEventBus.off(OnboardingEventKeys.end.redirect, handleCatalogRedirect);
    };
  }, [
    onboardingEventBus,
    user,
    stepMode,
    handleOnboardingView,
    handleStepNavigation,
    handleStep2Navigation,
    handleCatalogRedirect,
    handleStep3Navigation,
  ]);

  if (!accessToken) {
    return (
      <div className="flex justify-center items-center w-full h-full bg-black">
        {/* Placeholder o spinner */}
      </div>
    );
  }

  return (
    <>
      <section className="flex z-10 flex-col gap-4 self-center w-full max-w-[600px]">
        <OnboardingScreen
          step={step || 0}
          eventBus={onboardingEventBus}
          token={accessToken}
          dict={dict}
        />
      </section>
      <BackgroundWavesScreen eventBus={onboardingEventBus} />
    </>
  );
}

export default OnboardingContainer;
